import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, Download } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { CampaignEditor } from './CampaignEditor';

export function CampaignList() {
  const { api } = useAuth();
  const [list, setList] = useState(null);
  const load = useCallback(() => api.get('/reconnect/campaigns').then(r => setList(r.data)).catch(() => toast.error('Could not load campaigns')), [api]);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    try {
      await api.post('/reconnect/campaigns', { name: `Reconnect ${new Date().toLocaleDateString('en-NZ', { month: 'long', year: 'numeric' })}`, public_base_url: window.location.origin });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not create campaign'); }
  };
  const callList = async () => {
    try {
      const r = await api.get('/reconnect/call-list.csv', { responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(r.data); a.download = 'reconnect_call_list.csv'; a.click();
    } catch { toast.error('Download failed'); }
  };
  if (!list) return <div className="p-8 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div>;
  return (
    <div data-testid="campaign-list" className="space-y-4">
      <div className="flex gap-2">
        <button data-testid="campaign-new-btn" onClick={create} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded bg-[#D35400] text-white hover:bg-[#A04000]"><Plus size={14} />New campaign</button>
        <button data-testid="call-list-download-btn" onClick={callList} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded border border-slate-300 hover:bg-slate-50"><Download size={14} />Call / post list (no email)</button>
      </div>
      {list.length === 0 && <p className="text-sm text-slate-500">No campaigns yet. Create one — nothing sends until you send a test and press Start.</p>}
      {list.map(c => <CampaignEditor key={c._id} campaign={c} onChanged={load} onDeleted={load} />)}
    </div>
  );
}

export function RepliesList() {
  const { api } = useAuth();
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get('/reconnect/responses').then(r => setRows(r.data)).catch(() => setRows([])); }, [api]);
  if (!rows) return <div className="p-8 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div>;
  if (!rows.length) return <p className="text-sm text-slate-500" data-testid="replies-empty">No replies yet.</p>;
  return (
    <div data-testid="replies-list" className="border border-[#E2E2DF] rounded-md bg-white divide-y divide-[#F0EFEC]">
      {rows.map(r => (
        <div key={r._id} data-testid={`reply-${r._id}`} className="p-4 text-sm">
          <div className="flex flex-wrap gap-2 items-center">
            <a href={`/clients/${r.client_id}`} className="font-semibold text-slate-900 hover:underline">{r.client_name || 'Client'}</a>
            {r.book_inspection && <span className="px-2 py-0.5 text-[10px] font-bold uppercase rounded-full bg-emerald-100 text-emerald-800">Free inspection</span>}
            {r.changes?.length > 0 && <span className="px-2 py-0.5 text-[10px] font-bold uppercase rounded-full bg-sky-100 text-sky-800">{r.changes.length} detail{r.changes.length > 1 ? 's' : ''} updated</span>}
            <span className="ml-auto text-xs text-slate-400">{new Date(r.created_at).toLocaleString('en-NZ')}</span>
          </div>
          {r.services?.length > 0 && <p className="text-xs text-slate-600 mt-1">Interested in: {r.services.join(', ')}</p>}
          {r.changes?.length > 0 && <p className="text-xs text-slate-500 mt-1">{r.changes.map(ch => `${ch.field}: "${ch.old}" → "${ch.new}"`).join(' · ')}</p>}
          {(r.notes || r.best_time) && <p className="text-xs text-slate-600 mt-1">{r.best_time ? `Best time: ${r.best_time}. ` : ''}{r.notes}</p>}
          {r.web_lead_id && <a href="/web-leads" className="text-xs text-[#D35400] hover:underline mt-1 inline-block">Open in Website Enquiries</a>}
        </div>
      ))}
    </div>
  );
}
